import { Monitor } from "@/components/models/Monitor";
import { TableCell, TableRow } from "@/components/ui/Table";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { MoreHorizontal, Pause, Pencil, Play, Trash } from "lucide-react";
import { formatDuration, MonitorStatusLabel } from "@/components/MonitorHelpers";
import DurationTimer from "./DurationTimer";

interface MonitorTableRowProps {
  monitor?: Monitor;
  isLoading?: boolean;
  onEdit?: (monitor: Monitor) => void;
  onPause?: (monitorId: string) => void;
  onUnpause?: (monitorId: string) => void;
  onDelete?: (monitorId: string, monitorName: string) => void;
  onClick?: (monitor: Monitor) => void;
}

export default function MonitorTableRow({
  monitor,
  isLoading = false,
  onEdit,
  onPause,
  onUnpause,
  onDelete,
  onClick,
}: MonitorTableRowProps) {
  if (isLoading) {
    return (
      <TableRow>
        <TableCell>
          <div className="h-5 w-48 bg-muted animate-pulse rounded" />
        </TableCell>
        <TableCell>
          <div className="h-5 w-20 bg-muted animate-pulse rounded" />
        </TableCell>
        <TableCell className="hidden md:table-cell">
          <div className="h-5 w-24 bg-muted animate-pulse rounded" />
        </TableCell>
        <TableCell className="hidden lg:table-cell">
          <div className="h-5 w-20 bg-muted animate-pulse rounded" />
        </TableCell>
        <TableCell className="text-right">
          <div className="h-8 w-8 bg-muted animate-pulse rounded ml-auto" />
        </TableCell>
      </TableRow>
    );
  }

  if (!monitor) {
    return null;
  }

  return (
    <TableRow className="cursor-pointer hover:bg-muted/50" onClick={() => onClick?.(monitor)}>
      <TableCell>
        <div className="font-medium">{monitor.name}</div>
        {!monitor.active && (
          <Badge variant="outline" className="ml-2 text-xs">
            Paused
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <MonitorStatusLabel monitor={monitor} />
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">
        {monitor.last_status_change ? (
          <DurationTimer since={monitor.last_status_change} />
        ) : (
          <span className="text-muted-foreground">-</span>
        )}
      </TableCell>
      <TableCell className="hidden lg:table-cell">
        <span className="text-muted-foreground">{formatDuration(monitor.check_interval)}</span>
      </TableCell>
      <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onEdit?.(monitor)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit monitor
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => (monitor.active ? onPause?.(monitor.id) : onUnpause?.(monitor.id))}
            >
              {monitor.active ? (
                <>
                  <Pause className="mr-2 h-4 w-4" />
                  Pause alerts
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Resume alerts
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete?.(monitor.id, monitor.name)}
              className="text-destructive focus:text-destructive"
            >
              <Trash className="mr-2 h-4 w-4" />
              Delete monitor
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </TableCell>
    </TableRow>
  );
}
